import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Trail } from './Trail';
import { CoinShop } from './CoinShop';
import { Profile } from './Profile';
import { Achievements } from './Achievements';

/**
 * Player skin system — shapes + color palettes with unlock conditions.
 * Each shape has a matching trail style. Colors override the player's base fill.
 */
export class PlayerSkins {

  // ===== Shape definitions =====
  static readonly SHAPE_SQUARE = 0;    // default rounded rect
  static readonly SHAPE_CIRCLE = 1;
  static readonly SHAPE_TRIANGLE = 2;
  static readonly SHAPE_DIAMOND = 3;
  static readonly SHAPE_STAR = 4;
  static readonly SHAPE_HEXAGON = 5;
  static readonly SHAPE_COUNT = 6;

  static readonly SHAPE_NAMES: string[] = [
    'Square', 'Circle', 'Triangle', 'Diamond', 'Star', 'Hexagon',
  ];

  // Trail shape mapping — matches Trail.SHAPE_* constants
  // (built lazily: Trail must not be touched at module-import time)
  private static shapeTrail: number[] | null = null;

  static get SHAPE_TRAIL(): number[] {
    if (PlayerSkins.shapeTrail === null) {
      PlayerSkins.shapeTrail = [
        Trail.SHAPE_ROUND_RECT,  // square → round rect trail
        Trail.SHAPE_CIRCLE,      // circle → circle trail
        Trail.SHAPE_TRIANGLE,    // triangle → triangle trail
        Trail.SHAPE_DIAMOND,     // diamond → diamond trail
        Trail.SHAPE_CIRCLE,      // star → circle trail (stars too complex for trail)
        Trail.SHAPE_ROUND_RECT,  // hexagon → round rect trail
      ];
    }
    return PlayerSkins.shapeTrail;
  }

  // ===== Color palette definitions =====
  static readonly COLOR_DEFAULT = 0;   // white/light gray
  static readonly COLOR_NEON = 1;      // teal/cyan
  static readonly COLOR_EMBER = 2;     // warm orange
  static readonly COLOR_ROYAL = 3;     // purple
  static readonly COLOR_GHOST = 4;     // silver/ice blue
  static readonly COLOR_GOLDEN = 5;    // gold
  static readonly COLOR_CRIMSON = 6;   // deep red
  static readonly COLOR_LIME = 7;      // green
  static readonly COLOR_COUNT = 8;

  static readonly COLOR_NAMES: string[] = [
    'Default', 'Neon', 'Ember', 'Royal', 'Ghost', 'Golden', 'Crimson', 'Lime',
  ];

  // Base fill colors per palette
  static readonly COLOR_FILLS: Color[] = [
    new Color(230, 234, 240),  // default
    new Color(78, 205, 196),   // neon teal
    new Color(245, 170, 60),   // ember
    new Color(180, 120, 255),  // royal purple
    new Color(180, 200, 220),  // ghost silver
    new Color(255, 210, 80),   // golden
    new Color(220, 70, 80),    // crimson
    new Color(120, 220, 80),   // lime
  ];

  // Glow accent per palette (used for trail tint)
  static readonly COLOR_GLOWS: Color[] = [
    new Color(200, 210, 220),  // default
    new Color(100, 230, 220),  // neon
    new Color(255, 190, 80),   // ember
    new Color(200, 150, 255),  // royal
    new Color(160, 200, 240),  // ghost
    new Color(255, 225, 120),  // golden
    new Color(240, 100, 100),  // crimson
    new Color(140, 240, 100),  // lime
  ];

  // ===== Unlock conditions =====

  /** Check if a shape is unlocked */
  static isShapeUnlocked(shape: number): boolean {
    if (shape === PlayerSkins.SHAPE_SQUARE) return true;
    if (CoinShop.isShapeUnlockedByShop(shape)) return true;
    switch (shape) {
      case PlayerSkins.SHAPE_CIRCLE:   return Profile.getLevel() >= 3;
      case PlayerSkins.SHAPE_TRIANGLE: return Profile.getLevel() >= 6;
      case PlayerSkins.SHAPE_DIAMOND:  return Profile.getTotalGames() >= 25;
      case PlayerSkins.SHAPE_STAR:     return Profile.getLevel() >= 12;
      case PlayerSkins.SHAPE_HEXAGON:  return Profile.getTotalBossesDefeated() >= 10;
      default: return false;
    }
  }

  /** Get unlock description for a locked shape */
  static getShapeUnlockDesc(shape: number): string {
    switch (shape) {
      case PlayerSkins.SHAPE_CIRCLE:   return 'Reach profile level 3';
      case PlayerSkins.SHAPE_TRIANGLE: return 'Reach profile level 6';
      case PlayerSkins.SHAPE_DIAMOND:  return 'Play 25 games';
      case PlayerSkins.SHAPE_STAR:     return 'Reach profile level 12';
      case PlayerSkins.SHAPE_HEXAGON:  return 'Defeat 10 bosses';
      default: return '';
    }
  }

  /** Check if a color is unlocked */
  static isColorUnlocked(color: number): boolean {
    if (color === PlayerSkins.COLOR_DEFAULT) return true;
    if (CoinShop.isColorUnlockedByShop(color)) return true;
    switch (color) {
      case PlayerSkins.COLOR_NEON:     return Profile.getLevel() >= 2;
      case PlayerSkins.COLOR_EMBER:    return Profile.getLevel() >= 5;
      case PlayerSkins.COLOR_ROYAL:    return Profile.getLevel() >= 8;
      case PlayerSkins.COLOR_GHOST:    return Number(Profile.getTotalScore()) >= 100000;
      case PlayerSkins.COLOR_GOLDEN:   return Profile.getLevel() >= 15;
      case PlayerSkins.COLOR_CRIMSON:  return Achievements.getUnlockedCount() >= 20;
      case PlayerSkins.COLOR_LIME:     return Profile.getHighLevel(2) >= 10;
      default: return false;
    }
  }

  /** Get unlock description for a locked color */
  static getColorUnlockDesc(color: number): string {
    switch (color) {
      case PlayerSkins.COLOR_NEON:     return 'Reach profile level 2';
      case PlayerSkins.COLOR_EMBER:    return 'Reach profile level 5';
      case PlayerSkins.COLOR_ROYAL:    return 'Reach profile level 8';
      case PlayerSkins.COLOR_GHOST:    return 'Earn 100,000 total score';
      case PlayerSkins.COLOR_GOLDEN:   return 'Reach profile level 15';
      case PlayerSkins.COLOR_CRIMSON:  return 'Unlock 20 achievements';
      case PlayerSkins.COLOR_LIME:     return 'Reach level 10 on Insane';
      default: return '';
    }
  }

  // ===== Current selection (persisted in Profile) =====

  private static selectedShape: number = PlayerSkins.SHAPE_SQUARE;
  private static selectedColor: number = PlayerSkins.COLOR_DEFAULT;

  static getSelectedShape(): number { return PlayerSkins.selectedShape; }
  static getSelectedColor(): number { return PlayerSkins.selectedColor; }
  static getSelectedFill(): Color { return PlayerSkins.COLOR_FILLS[PlayerSkins.selectedColor]; }
  static getSelectedGlow(): Color { return PlayerSkins.COLOR_GLOWS[PlayerSkins.selectedColor]; }
  static getSelectedTrailShape(): number { return PlayerSkins.SHAPE_TRAIL[PlayerSkins.selectedShape]; }

  static setSelectedShape(shape: number): void {
    if (shape >= 0 && shape < PlayerSkins.SHAPE_COUNT && PlayerSkins.isShapeUnlocked(shape)) {
      PlayerSkins.selectedShape = shape;
      Profile.saveSkinSelection();
    }
  }

  static setSelectedColor(color: number): void {
    if (color >= 0 && color < PlayerSkins.COLOR_COUNT && PlayerSkins.isColorUnlocked(color)) {
      PlayerSkins.selectedColor = color;
      Profile.saveSkinSelection();
    }
  }

  // Called by Profile on load/save
  static getShapeId(): number { return PlayerSkins.selectedShape; }
  static getColorId(): number { return PlayerSkins.selectedColor; }

  static loadSelection(shape: number, color: number): void {
    PlayerSkins.selectedShape = (shape >= 0 && shape < PlayerSkins.SHAPE_COUNT) ? shape : PlayerSkins.SHAPE_SQUARE;
    PlayerSkins.selectedColor = (color >= 0 && color < PlayerSkins.COLOR_COUNT) ? color : PlayerSkins.COLOR_DEFAULT;
  }

  // ===== Drawing helpers =====

  /** Draw the player shape at (ix, iy) with given size, corner radius, and color */
  static drawShape(g: Graphics2D, shape: number, ix: number, iy: number, size: number, r: number, fill: Color): void {
    const cx = ix + Math.trunc(size / 2), cy = iy + Math.trunc(size / 2);
    const half = Math.trunc(size / 2);
    g.setColor(fill);

    switch (shape) {
      case PlayerSkins.SHAPE_CIRCLE:
        g.fillOval(ix, iy, size, size);
        break;
      case PlayerSkins.SHAPE_TRIANGLE: {
        const xp: number[] = new Array<number>(3).fill(0);
        const yp: number[] = new Array<number>(3).fill(0);
        for (let i = 0; i < 3; i++) {
          const a = Math.fround(-Math.PI / 2 + i * Math.PI * 2 / 3);
          xp[i] = cx + Math.trunc(Math.cos(a) * half);
          yp[i] = cy + Math.trunc(Math.sin(a) * half);
        }
        g.fillPolygon(xp, yp, 3);
        break;
      }
      case PlayerSkins.SHAPE_DIAMOND:
        g.fillPolygon(
          [cx, cx + half, cx, cx - half],
          [cy - half, cy, cy + half, cy], 4);
        break;
      case PlayerSkins.SHAPE_STAR: {
        const points = 5;
        const xp: number[] = new Array<number>(points * 2).fill(0);
        const yp: number[] = new Array<number>(points * 2).fill(0);
        for (let i = 0; i < points * 2; i++) {
          const a = Math.fround(-Math.PI / 2 + i * Math.PI / points);
          const dist = (i % 2 === 0) ? half : Math.fround(half * Math.fround(0.45));
          xp[i] = cx + Math.trunc(Math.cos(a) * dist);
          yp[i] = cy + Math.trunc(Math.sin(a) * dist);
        }
        g.fillPolygon(xp, yp, points * 2);
        break;
      }
      case PlayerSkins.SHAPE_HEXAGON: {
        const xp: number[] = new Array<number>(6).fill(0);
        const yp: number[] = new Array<number>(6).fill(0);
        for (let i = 0; i < 6; i++) {
          const a = Math.fround(i * Math.PI / 3);
          xp[i] = cx + Math.trunc(Math.cos(a) * half);
          yp[i] = cy + Math.trunc(Math.sin(a) * half);
        }
        g.fillPolygon(xp, yp, 6);
        break;
      }
      default: // SHAPE_SQUARE
        g.fillRoundRect(ix, iy, size, size, r, r);
        break;
    }
  }

  /** Draw outline of the shape */
  static drawShapeOutline(g: Graphics2D, shape: number, ix: number, iy: number, size: number, r: number, color: Color): void {
    const cx = ix + Math.trunc(size / 2), cy = iy + Math.trunc(size / 2);
    const half = Math.trunc(size / 2);
    g.setColor(color);

    switch (shape) {
      case PlayerSkins.SHAPE_CIRCLE:
        g.drawOval(ix, iy, size, size);
        break;
      case PlayerSkins.SHAPE_TRIANGLE: {
        const xp: number[] = new Array<number>(3).fill(0);
        const yp: number[] = new Array<number>(3).fill(0);
        for (let i = 0; i < 3; i++) {
          const a = Math.fround(-Math.PI / 2 + i * Math.PI * 2 / 3);
          xp[i] = cx + Math.trunc(Math.cos(a) * half);
          yp[i] = cy + Math.trunc(Math.sin(a) * half);
        }
        g.drawPolygon(xp, yp, 3);
        break;
      }
      case PlayerSkins.SHAPE_DIAMOND:
        g.drawPolygon(
          [cx, cx + half, cx, cx - half],
          [cy - half, cy, cy + half, cy], 4);
        break;
      case PlayerSkins.SHAPE_STAR: {
        const points = 5;
        const xp: number[] = new Array<number>(points * 2).fill(0);
        const yp: number[] = new Array<number>(points * 2).fill(0);
        for (let i = 0; i < points * 2; i++) {
          const a = Math.fround(-Math.PI / 2 + i * Math.PI / points);
          const dist = (i % 2 === 0) ? half : Math.fround(half * Math.fround(0.45));
          xp[i] = cx + Math.trunc(Math.cos(a) * dist);
          yp[i] = cy + Math.trunc(Math.sin(a) * dist);
        }
        g.drawPolygon(xp, yp, points * 2);
        break;
      }
      case PlayerSkins.SHAPE_HEXAGON: {
        const xp: number[] = new Array<number>(6).fill(0);
        const yp: number[] = new Array<number>(6).fill(0);
        for (let i = 0; i < 6; i++) {
          const a = Math.fround(i * Math.PI / 3);
          xp[i] = cx + Math.trunc(Math.cos(a) * half);
          yp[i] = cy + Math.trunc(Math.sin(a) * half);
        }
        g.drawPolygon(xp, yp, 6);
        break;
      }
      default:
        g.drawRoundRect(ix, iy, size, size, r, r);
        break;
    }
  }
}
